import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface ImageGalleryProps {
  images: string[]
  alt: string
  variant?: 'light' | 'dark'
}

export function ImageGallery({ images, alt, variant = 'light' }: ImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  if (images.length === 0) return null

  const isDark = variant === 'dark'

  return (
    <div className="space-y-4">
      <div
        className={`rounded-lg p-6 flex items-center justify-center min-h-[400px] ${
          isDark ? 'bg-white/5 border border-white/10' : 'bg-surface-light'
        }`}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={selectedIndex}
            src={images[selectedIndex]}
            alt={alt}
            className="max-w-full max-h-[500px] object-contain"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            loading="eager"
          />
        </AnimatePresence>
      </div>

      {images.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setSelectedIndex(index)}
              className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-colors ${
                index === selectedIndex
                  ? 'border-accent'
                  : isDark
                    ? 'border-white/10 hover:border-white/30'
                    : 'border-transparent hover:border-border-light'
              }`}
            >
              <img
                src={image}
                alt={`${alt} ${index + 1}`}
                className={`w-full h-full object-contain p-1 ${
                  isDark ? 'bg-white/5' : 'bg-surface-light'
                }`}
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
